import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, UploadResult } from 'firebase/storage';

export interface UploadProgress {
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
  currentFile?: number;
  totalFiles?: number;
}

export class ImageUploadService {
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  /**
   * Validates the uploaded file
   */
  static validateFile(file: File): { isValid: boolean; error?: string } {
    if (!file) {
      return { isValid: false, error: 'No file selected' };
    }

    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return { 
        isValid: false, 
        error: 'Invalid file type. Please upload a JPEG, PNG, or WebP image.' 
      };
    }

    if (file.size > this.MAX_FILE_SIZE) {
      return { 
        isValid: false, 
        error: 'File too large. Please upload an image smaller than 5MB.' 
      };
    }

    return { isValid: true };
  }

  /**
   * Compresses an image file to reduce size
   */
  static async compressImage(file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;

        // Set canvas dimensions
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, newWidth, newHeight);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Generates a unique filename for the uploaded image
   */
  static generateFileName(originalName: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `plates/${timestamp}_${randomString}.${extension}`;
  }

  /**
   * Uploads an image to Firebase Storage
   */
  static async uploadImage(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<string> {
    try {
      // Validate file
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Compress image
      onProgress?.({ progress: 10, status: 'uploading' });
      const compressedFile = await this.compressImage(file);
      
      // Generate unique filename
      const fileName = this.generateFileName(file.name);
      const storageRef = ref(storage, fileName);

      // Upload to Firebase Storage
      onProgress?.({ progress: 30, status: 'uploading' });
      const uploadResult: UploadResult = await uploadBytes(storageRef, compressedFile);
      
      onProgress?.({ progress: 80, status: 'uploading' });
      
      // Get download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      onProgress?.({ progress: 100, status: 'success' });
      
      return downloadURL;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      onProgress?.({ progress: 0, status: 'error', error: errorMessage });
      throw error;
    }
  }

  /**
   * Uploads multiple images sequentially with progress tracking
   */
  static async uploadMultipleImages(
    files: File[],
    onProgress?: (progress: UploadProgress) => void
  ): Promise<string[]> {
    const urls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const baseProgress = (i / files.length) * 100;
      const fileWeight = 100 / files.length;

      const url = await this.uploadImage(file, (progress) => {
        const overallProgress = baseProgress + (progress.progress / 100) * fileWeight;
        onProgress?.({
          ...progress,
          progress: overallProgress,
          currentFile: i + 1,
          totalFiles: files.length,
        });
      });

      urls.push(url);
    }

    return urls;
  }
}
